import React, { useEffect, useState, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  FormHelperText,
} from "@mui/material";

interface Option {
  label: string;
  value: string | number;
}

interface AsyncAutoCompleteProps {
  label: string;
  value: string | number | null;
  onChange: (value: any) => void;
  options: Option[];
  onSearch: (text: string) => void;
  onScrollEnd?: () => void;
  loading?: boolean;
  error?: boolean;
  helperText?: string | null;
  required?: boolean;
  disabled?: boolean;
  // Pass the currently selected item explicitly so it always renders
  // even if it hasn't been loaded into the paginated options list yet
  currentOption?: Option | null;
}

export const AsyncAutoComplete: React.FC<AsyncAutoCompleteProps> = ({
  label,
  value,
  onChange,
  options,
  onSearch,
  onScrollEnd,
  loading = false,
  error,
  helperText,
  required,
  disabled,
  currentOption,
}) => {
  const [search, setSearch] = useState("");

  // Keep latest callback in a ref so the scroll handler never captures a stale closure
  const onScrollEndRef = useRef(onScrollEnd);
  useEffect(() => { onScrollEndRef.current = onScrollEnd; }, [onScrollEnd]);

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const savedScrollTopRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const prevOptionsLengthRef = useRef(0);
  const isRestoringScrollRef = useRef(false);
  const isOpenRef = useRef(false);

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    const delay = setTimeout(() => onSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search, onSearch]);

  // ─── Stable scroll handler ────────────────────────────────────────────────
  const handleScroll = useRef((e: Event) => {
    if (isRestoringScrollRef.current) return;

    const target = e.target as HTMLElement;
    savedScrollTopRef.current = target.scrollTop;

    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= 150 && !isFetchingRef.current) {
      isFetchingRef.current = true;
      onScrollEndRef.current?.();
    }
  }).current;

  // ─── Find the MUI menu scroll container ──────────────────────────────────
  const findScrollContainer = (): HTMLElement | null => {
    const candidates = Array.from(
      document.querySelectorAll<HTMLElement>(".MuiMenu-paper, .MuiPopover-paper")
    );
    for (const el of candidates) {
      if (el.scrollHeight > el.clientHeight) return el;
    }
    const listbox = document.querySelector<HTMLElement>("[role='listbox']");
    if (listbox && listbox.scrollHeight > listbox.clientHeight) return listbox;
    return null;
  };

  const attachScrollListener = () => {
    setTimeout(() => {
      const container = findScrollContainer();
      if (container) {
        container.removeEventListener("scroll", handleScroll);
        container.addEventListener("scroll", handleScroll, { passive: true });
        scrollContainerRef.current = container;
      }
    }, 150);
  };

  const detachScrollListener = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      scrollContainerRef.current = null;
    }
  };

  // ─── Unlock + restore scroll when options grow ────────────────────────────
  useEffect(() => {
    const prevLen = prevOptionsLengthRef.current;
    const newLen = options.length;

    if (newLen > prevLen && isOpenRef.current) {
      const savedTop = savedScrollTopRef.current;

      if (savedTop > 0) {
        isRestoringScrollRef.current = true;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            const container = scrollContainerRef.current ?? findScrollContainer();
            if (container) {
              scrollContainerRef.current = container;
              container.scrollTop = savedTop;
            }
            setTimeout(() => {
              isRestoringScrollRef.current = false;
              isFetchingRef.current = false;
            }, 50);
          })
        );
      } else {
        isFetchingRef.current = false;
      }
    }

    prevOptionsLengthRef.current = newLen;
  }, [options.length]);

  // ─── Menu open / close ────────────────────────────────────────────────────
  const handleMenuOpen = () => {
    isOpenRef.current = true;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    savedScrollTopRef.current = 0;
    prevOptionsLengthRef.current = options.length;
    attachScrollListener();
  };

  const handleMenuClose = () => {
    isOpenRef.current = false;
    isFetchingRef.current = false;
    isRestoringScrollRef.current = false;
    detachScrollListener();
  };

  useEffect(() => () => detachScrollListener(), []);

  // ─── Merge currentOption into options list ────────────────────────────────
  // If the selected value isn't in the current paginated options (e.g. it's on
  // a page not yet loaded), inject it so MUI can render its label correctly.
  const mergedOptions: Option[] = React.useMemo(() => {
    if (!currentOption || !value) return options;
    const alreadyInList = options.some((o) => o.value === currentOption.value);
    if (alreadyInList) return options;
    // Prepend so it's visible at top of list
    return [currentOption, ...options];
  }, [options, currentOption, value]);

  return (
    <FormControl fullWidth error={error} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>

      <Select
        value={value ?? ""}
        label={label}
        disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.value)}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
            },
          },
        }}
      >
        {!disabled && (
          <Box
            p={1}
            position="sticky"
            top={0}
            zIndex={1}
            bgcolor="background.paper"
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              placeholder="Search..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus
            />
          </Box>
        )}

        {mergedOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}

        {loading && (
          <MenuItem
            disabled
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              justifyContent: "center",
              py: 1,
            }}
          >
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading more...
          </MenuItem>
        )}

        {!loading && mergedOptions.length === 0 && !disabled && (
          <MenuItem disabled>
            <em>No options available</em>
          </MenuItem>
        )}

        {disabled && (
          <MenuItem disabled>
            <em>Select a Region first</em>
          </MenuItem>
        )}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};